import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import AsyncSelect from "react-select/async";
import ModalPopup from "../../components/ModalPopup";
import { COLORS } from "../../utils/colors";
import { api } from "../../utils/api";
import { fetchVehicleDetails } from "../../utils/fetchVehicleDetails";
import {
  getFieldsFromNevdisDetails,
  VEHICLE_TYPE_OPTIONS
} from "../../utils/mapNevdisBodyType";
import {
  getVehicleStateFieldLabel,
  getVehicleStateOptionsForSelect,
  isAuVehicleState
} from "../../utils/p1Region";

const EMPTY_VEHICLE_FORM = {
  rego: "",
  state: "",
  vin: "",
  make: "",
  model: "",
  modelYear: "",
  color: "",
  vehicleType: "",
  active: true
};

const customerLabel = (customer) => {
  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ");
  const email = customer?.email ? ` (${customer.email})` : "";
  return name ? `${name}${email}` : customer?.email || customer?.phone || "Customer";
};

const toCustomerOption = (customer) => {
  if (!customer?._id) {
    return null;
  }

  const address = customer.address || {};
  const addressLine = [address.street, address.city, address.state, address.postcode]
    .filter(Boolean)
    .join(", ");

  return {
    value: customer._id,
    label: customerLabel(customer),
    customer,
    meta: {
      phone: customer.phone || "",
      address: addressLine
    }
  };
};

const customerSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "white",
    borderColor: "#cbd5e1",
    minHeight: 42,
    boxShadow: "none",
    "&:hover": {
      borderColor: "#94a3b8"
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 20
  })
};

const CreateMembership = () => {
  const navigate = useNavigate();
  const isAdminView = sessionStorage.getItem("selectedPortal") === "Administrator";
  const [step, setStep] = useState(1);
  const [customerMode, setCustomerMode] = useState("existing");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [vehicleMode, setVehicleMode] = useState("existing");
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [loadingCustomerVehicles, setLoadingCustomerVehicles] = useState(false);
  const [newVehicleForm, setNewVehicleForm] = useState({ ...EMPTY_VEHICLE_FORM });
  const [newVehicleError, setNewVehicleError] = useState(null);
  const [vehicleLookupStatus, setVehicleLookupStatus] = useState(null);
  const [vehicleSubmitting, setVehicleSubmitting] = useState(false);
  const vehicleLookupTimeoutRef = useRef(null);
  const [newCustomerVehicleForm, setNewCustomerVehicleForm] = useState({ ...EMPTY_VEHICLE_FORM });
  const [newCustomerVehicleError, setNewCustomerVehicleError] = useState(null);
  const [newCustomerForm, setNewCustomerForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    postcode: "",
    country: "",
    active: true
  });
  const [newCustomerError, setNewCustomerError] = useState(null);
  const [customerSubmitting, setCustomerSubmitting] = useState(false);

  const [subscriptionMode, setSubscriptionMode] = useState("existing");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionOptions, setSubscriptionOptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [partnerCompanies, setPartnerCompanies] = useState([]);
  const [loadingPartnerCompanies, setLoadingPartnerCompanies] = useState(false);
  const [selectedPartnerCompany, setSelectedPartnerCompany] = useState(null);
  const [partnerCompanySearch, setPartnerCompanySearch] = useState("");
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [loadingPartnerUsers, setLoadingPartnerUsers] = useState(false);
  const [selectedPartnerUser, setSelectedPartnerUser] = useState(null);
  const [partnerUserSearch, setPartnerUserSearch] = useState("");
  const [merchantStores, setMerchantStores] = useState([]);
  const [loadingMerchantStores, setLoadingMerchantStores] = useState(false);
  const [selectedMerchantStore, setSelectedMerchantStore] = useState(null);
  const [merchantStoreSearch, setMerchantStoreSearch] = useState("");
  const [storeStaff, setStoreStaff] = useState([]);
  const [selectedStoreStaff, setSelectedStoreStaff] = useState(null);
  const [storeStaffSearch, setStoreStaffSearch] = useState("");
  const [newSubscriptionForm, setNewSubscriptionForm] = useState({
    productId: "",
    startDateUTC: "",
    validUntilDateUTC: "",
    nextBillingDateUTC: "",
    active: true,
    handledByStripe: false,
    paymentRequired: false,
    rrp: ""
  });
  const [newSubscriptionError, setNewSubscriptionError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [savingMembership, setSavingMembership] = useState(false);
  const [saveMembershipError, setSaveMembershipError] = useState(null);
  const [paymentLinkModal, setPaymentLinkModal] = useState({
    open: false,
    message: "",
    url: "",
    subscriptionId: null,
  });
  const [resultSuccessModal, setResultSuccessModal] = useState({
    open: false,
    title: "",
    message: "",
    subscriptionId: null,
  });
  const [resultErrorModal, setResultErrorModal] = useState({
    open: false,
    message: "",
  });
  const [identityConflictModal, setIdentityConflictModal] = useState({
    open: false,
    message: "",
    details: null,
  });
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchPartnerCompanies();
    fetchMerchantStores();
  }, []);

  useEffect(() => {
    return () => {
      if (vehicleLookupTimeoutRef.current) {
        clearTimeout(vehicleLookupTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedCustomer) {
      setSubscriptionOptions([]);
      setSelectedSubscription(null);
      setCustomerVehicles([]);
      setSelectedVehicle(null);
      return;
    }

    if (!selectedCustomer._id) {
      setSubscriptionOptions([]);
      setSelectedSubscription(null);
      if (!selectedCustomer._draft) {
        setCustomerVehicles([]);
        setSelectedVehicle(null);
      }
      return;
    }

    fetchSubscriptions(selectedCustomer._id);
    fetchCustomerVehicles(selectedCustomer._id);
  }, [selectedCustomer]);

  const fetchCustomers = async (searchTerm = "") => {
    setLoadingCustomers(true);
    try {
      const response = await api("v1/customer/search", "POST", {
        query: searchTerm ? { searchTerm } : {},
        page: 1,
        limit: 25
      });

      if (response.data.success) {
        const results = response.data.data.searchResult || [];
        const options = results.map(toCustomerOption).filter(Boolean);
        setCustomerOptions(options);
        return options;
      }

      setCustomerOptions([]);
      return [];
    } catch (error) {
      console.error("Error loading customers:", error);
      setCustomerOptions([]);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadCustomerOptions = async (inputValue) => {
    return fetchCustomers(inputValue.trim());
  };

  const fetchCustomerVehicles = async (customerId) => {
    setLoadingCustomerVehicles(true);
    try {
      const response = await api(`v1/vehicle/customer/${customerId}`, "GET", null);
      if (!response.data.success) {
        setCustomerVehicles([]);
        return;
      }

      const allVehicles = Array.isArray(response.data.data?.allVehicles)
        ? response.data.data.allVehicles
        : [];

      const vehicles = await Promise.all(
        allVehicles.map(async (item) => {
          if (!item) return null;
          if (typeof item === "object" && item._id) return item;
          const vehicleId = typeof item === "string" ? item : null;
          if (!vehicleId) return null;
          const vehicleResponse = await api(`v1/vehicle/${vehicleId}`, "GET", null);
          return vehicleResponse.data.success ? vehicleResponse.data.data.vehicle : null;
        })
      );

      setCustomerVehicles(vehicles.filter(Boolean));
    } catch (error) {
      console.error("Error loading customer vehicles:", error);
      setCustomerVehicles([]);
    } finally {
      setLoadingCustomerVehicles(false);
    }
  };

  const fetchSubscriptions = async (customerId) => {
    setLoadingSubscriptions(true);
    try {
      const response = await api(`v1/subscription/customer/${customerId}`, "GET");
      if (response.data.success) {
        setSubscriptionOptions(response.data.data.allSubscriptions || []);
      } else {
        setSubscriptionOptions([]);
      }
    } catch (error) {
      console.error("Error loading subscriptions:", error);
      setSubscriptionOptions([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api("v1/product", "GET", null);
      if (response.data.success) {
        setProductOptions(response.data.data.allProducts || []);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchPartnerCompanies = async () => {
    setLoadingPartnerCompanies(true);
    try {
      const response = await api("v1/partner/company", "GET", null);
      if (response.data.success) {
        setPartnerCompanies(response.data.data.allCompanies || []);
      }
    } catch (error) {
      console.error("Error loading partner companies:", error);
      setPartnerCompanies([]);
    } finally {
      setLoadingPartnerCompanies(false);
    }
  };

  const fetchPartnerUsers = async (companyId) => {
    if (!companyId) {
      setPartnerUsers([]);
      setSelectedPartnerUser(null);
      setPartnerUserSearch("");
      return;
    }

    setLoadingPartnerUsers(true);
    try {
      const response = await api(`v1/partner/company/${companyId}/user`, "GET", null);
      if (response.data.success) {
        const users = (response.data.data.partnerUsers || []).filter((user) => user?.active !== false);
        setPartnerUsers(users);
      } else {
        setPartnerUsers([]);
      }
    } catch (error) {
      console.error("Error loading partner users:", error);
      setPartnerUsers([]);
    } finally {
      setLoadingPartnerUsers(false);
    }
  };

  const fetchMerchantStores = async () => {
    setLoadingMerchantStores(true);
    try {
      const response = await api("v1/merchantStore", "GET", null);
      if (response.data.success) {
        setMerchantStores(response.data.data.allMerchantStores || []);
      } else {
        setMerchantStores([]);
      }
    } catch (error) {
      console.error("Error loading merchant stores:", error);
      setMerchantStores([]);
    } finally {
      setLoadingMerchantStores(false);
    }
  };

  const storeAddressLabel = (store) => {
    const address = store?.address || {};
    return [address.street, address.city, address.state, address.postcode].filter(Boolean).join(", ");
  };

  const storeStaffLabel = (staff) => staff?.label || staff?.name || "Store staff";

  const clearStoreSelection = () => {
    setSelectedMerchantStore(null);
    setMerchantStoreSearch("");
    setStoreStaff([]);
    setSelectedStoreStaff(null);
    setStoreStaffSearch("");
  };

  const handleCustomerSelect = (option) => {
    setSelectedCustomerOption(option);
    setSelectedCustomer(option?.customer || null);
  };

  const goToStepTwo = () => {
    if (customerMode === "new") {
      const created = handleCreateCustomer();
      if (!created) {
        return;
      }
    } else if (!selectedCustomer) {
      alert("Please select or create a customer first.");
      return;
    }

    setStep(2);
  };

  const handleSubscriptionSelect = (event) => {
    const subscription = subscriptionOptions.find((item) => item._id === event.target.value) || null;
    setSelectedSubscription(subscription);
  };

  const handleSubscriptionCreated = (createdSubscription) => {
    setSelectedSubscription(createdSubscription);
    setSubscriptionMode("existing");
    setStep(3);
  };

  const handleVehicleCreated = (createdVehicle) => {
    setSelectedVehicle(createdVehicle);
    if (createdVehicle?.customer?._id || selectedCustomer?._id) {
      fetchCustomerVehicles(createdVehicle?.customer?._id || selectedCustomer._id);
    }
  };

  const lookupVehicleFromRegoState = async (rego, state) => {
    if (!rego || !state) {
      return;
    }

    if (!isAuVehicleState(state)) {
      setVehicleLookupStatus(null);
      return;
    }

    setVehicleLookupStatus("Looking up vehicle details…");
    try {
      const vehicleDetails = await fetchVehicleDetails(rego, state);
      if (!vehicleDetails) {
        setVehicleLookupStatus("No vehicle found for that rego and state. Enter details manually.");
        return;
      }

      const fields = getFieldsFromNevdisDetails(vehicleDetails);

      setNewVehicleForm((prev) => ({
        ...prev,
        vin: fields.vin || prev.vin,
        make: fields.make || prev.make,
        model: fields.model || prev.model,
        modelYear: fields.modelYear || prev.modelYear,
        color: fields.color || prev.color,
        vehicleType: fields.vehicleType || prev.vehicleType
      }));

      if (fields.vehicleType) {
        const typeLabel =
          VEHICLE_TYPE_OPTIONS.find((option) => option.value === fields.vehicleType)?.label ||
          fields.vehicleType;
        setVehicleLookupStatus(
          `Vehicle details found (${fields.bodyType} → ${typeLabel}). Review and complete any remaining fields.`
        );
      } else {
        setVehicleLookupStatus(
          fields.bodyType
            ? `Vehicle details found. Could not map body type "${fields.bodyType}" — please select vehicle type manually.`
            : "Vehicle details found and filled in. Review and complete any remaining fields."
        );
      }
    } catch (error) {
      console.error("Vehicle lookup error:", error);
      setVehicleLookupStatus("Could not auto-find vehicle details. Enter them manually.");
    }
  };

  const handleNewVehicleFieldChange = (field, value) => {
    setNewVehicleForm((prev) => ({ ...prev, [field]: value }));

    if (field === "rego" || field === "state") {
      const nextRego = field === "rego" ? value : newVehicleForm.rego;
      const nextState = field === "state" ? value : newVehicleForm.state;

      if (vehicleLookupTimeoutRef.current) {
        clearTimeout(vehicleLookupTimeoutRef.current);
      }

      if (nextRego && nextState) {
        vehicleLookupTimeoutRef.current = setTimeout(() => {
          lookupVehicleFromRegoState(nextRego, nextState);
        }, 500);
      } else {
        setVehicleLookupStatus(null);
      }
    }
  };

  const handleNewSubscriptionFieldChange = (field, value) => {
    setNewSubscriptionForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedProduct = productOptions.find((item) => item._id === newSubscriptionForm.productId) || null;

  const handleProductSelect = (product) => {
    handleNewSubscriptionFieldChange("productId", product?._id || "");
    setProductSearch(product?.name || "");
  };

  const handleProductSearchChange = (value) => {
    setProductSearch(value);
    if (selectedProduct && value !== selectedProduct.name) {
      handleNewSubscriptionFieldChange("productId", "");
    }
  };

  const filteredProductOptions = productOptions.filter((product) => {
    const term = productSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return product.name?.toLowerCase().includes(term);
  });

  useEffect(() => {
    if (selectedSubscription?._draft) {
      const draftProductId =
        selectedSubscription.product?._id || selectedSubscription.product?.value || "";
      setNewSubscriptionForm({
        productId: draftProductId,
        startDateUTC: selectedSubscription.startDateUTC || "",
        validUntilDateUTC: selectedSubscription.validUntilDateUTC || "",
        nextBillingDateUTC: selectedSubscription.nextBillingDateUTC || "",
        active: selectedSubscription.active ?? true,
        handledByStripe: selectedSubscription.createInStripe || false,
        paymentRequired: selectedSubscription.paymentRequired || false,
        rrp: selectedSubscription.rrp || ""
      });
      setProductSearch(selectedSubscription.product?.name || "");
    }
  }, [selectedSubscription]);

  const handleCreateVehicle = (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (!selectedCustomer) {
      setNewVehicleError("Select a customer before adding a vehicle.");
      return false;
    }
    if (
      !newVehicleForm.vin ||
      !newVehicleForm.rego ||
      !newVehicleForm.state ||
      !newVehicleForm.make ||
      !newVehicleForm.model ||
      !newVehicleForm.modelYear ||
      !newVehicleForm.color ||
      !newVehicleForm.vehicleType
    ) {
      setNewVehicleError("Please complete all required vehicle fields.");
      return false;
    }

    setNewVehicleError(null);
    setVehicleLookupStatus(null);

    setSelectedVehicle({
      ...newVehicleForm,
      customer: selectedCustomer._id || null,
      _draft: true
    });
    setShowVehicleModal(false);
    setNewVehicleForm({ ...EMPTY_VEHICLE_FORM });
    return true;
  };

  const handleCreateSubscriptionDraft = (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (!selectedVehicle) {
      setNewSubscriptionError("Please select a vehicle before creating a new subscription.");
      return false;
    }

    const requiredFields = ["productId", "startDateUTC", "validUntilDateUTC", "nextBillingDateUTC"];
    const missingField = requiredFields.find((field) => !newSubscriptionForm[field]);
    if (missingField) {
      setNewSubscriptionError("Please complete all required subscription fields.");
      return;
    }

    if (newSubscriptionForm.handledByStripe && (newSubscriptionForm.rrp === "" || newSubscriptionForm.rrp == null)) {
      setNewSubscriptionError("Please enter the amount the customer will pay via Stripe.");
      return false;
    }

    const product = productOptions.find((item) => item._id === newSubscriptionForm.productId) || null;
    if (!product) {
      setNewSubscriptionError("Please select a valid product.");
      return false;
    }

    setNewSubscriptionError(null);

    setSelectedSubscription({
      product,
      vehicle: selectedVehicle,
      startDateUTC: newSubscriptionForm.startDateUTC,
      validUntilDateUTC: newSubscriptionForm.validUntilDateUTC,
      nextBillingDateUTC: newSubscriptionForm.nextBillingDateUTC,
      // Active when Stripe is off (partner-collected payment); inactive when Stripe until paid
      active: !newSubscriptionForm.handledByStripe,
      createdOnUTC: new Date().toISOString(),
      createInStripe: newSubscriptionForm.handledByStripe,
      paymentRequired: Boolean(newSubscriptionForm.handledByStripe),
      rrp: newSubscriptionForm.rrp,
      _draft: true
    });
    return true;
  };

  const handleNewCustomerFieldChange = (field, value) => {
    setNewCustomerForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewCustomerVehicleFieldChange = (field, value) => {
    setNewCustomerVehicleForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePartnerCompanySelect = (company) => {
    setSelectedPartnerCompany(company);
    setPartnerCompanySearch(company?.name || "");
    setSelectedPartnerUser(null);
    setPartnerUserSearch("");
    clearStoreSelection();
    fetchPartnerUsers(company?._id);
  };

  const handlePartnerCompanySearchChange = (value) => {
    setPartnerCompanySearch(value);
    if (selectedPartnerCompany && value !== selectedPartnerCompany.name) {
      setSelectedPartnerCompany(null);
      setSelectedPartnerUser(null);
      setPartnerUserSearch("");
      setPartnerUsers([]);
      clearStoreSelection();
    }
  };

  const handleMerchantStoreSelect = (store) => {
    setSelectedMerchantStore(store);
    setMerchantStoreSearch(store?.name || "");
    const staff = Array.isArray(store?.staff) ? store.staff.filter((entry) => entry?.value || entry?._id) : [];
    setStoreStaff(staff);
    setSelectedStoreStaff(null);
    setStoreStaffSearch("");
  };

  const handleMerchantStoreSearchChange = (value) => {
    setMerchantStoreSearch(value);
    if (selectedMerchantStore && value !== selectedMerchantStore.name) {
      setSelectedMerchantStore(null);
      setStoreStaff([]);
      setSelectedStoreStaff(null);
      setStoreStaffSearch("");
    }
  };

  const handleStoreStaffSelect = (staff) => {
    setSelectedStoreStaff(staff);
    setStoreStaffSearch(storeStaffLabel(staff));
  };

  const handleStoreStaffSearchChange = (value) => {
    setStoreStaffSearch(value);
    if (selectedStoreStaff && value !== storeStaffLabel(selectedStoreStaff)) {
      setSelectedStoreStaff(null);
    }
  };

  const partnerUserLabel = (user) => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    return name || user?.email || user?.phone || "Partner user";
  };

  const handlePartnerUserSelect = (user) => {
    setSelectedPartnerUser(user);
    setPartnerUserSearch(partnerUserLabel(user));
  };

  const handlePartnerUserSearchChange = (value) => {
    setPartnerUserSearch(value);
    if (selectedPartnerUser && value !== partnerUserLabel(selectedPartnerUser)) {
      setSelectedPartnerUser(null);
    }
  };

  const filteredPartnerCompanies = partnerCompanies.filter((company) => {
    const term = partnerCompanySearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return (
      company.name?.toLowerCase().includes(term) ||
      company.email?.toLowerCase().includes(term) ||
      company.phone?.toLowerCase().includes(term)
    );
  });

  const filteredMerchantStores = merchantStores.filter((store) => {
    const term = merchantStoreSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return (
      store.name?.toLowerCase().includes(term) ||
      storeAddressLabel(store).toLowerCase().includes(term)
    );
  });

  const filteredStoreStaff = storeStaff.filter((staff) => {
    const term = storeStaffSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return storeStaffLabel(staff).toLowerCase().includes(term);
  });

  const filteredPartnerUsers = partnerUsers.filter((user) => {
    const term = partnerUserSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return (
      partnerUserLabel(user).toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.phone?.toLowerCase().includes(term)
    );
  });

  const handleCreateCustomer = (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const requiredFields = ["firstName", "lastName", "email", "phone", "street", "city", "state", "postcode", "country"];
    const missingField = requiredFields.find((field) => !newCustomerForm[field]);
    if (missingField) {
      setNewCustomerError("Please complete all required customer fields.");
      return false;
    }

    setNewCustomerError(null);
    setNewCustomerVehicleError(null);

    const draftCustomer = {
      ...newCustomerForm,
      address: {
        street: newCustomerForm.street,
        city: newCustomerForm.city,
        state: newCustomerForm.state,
        postcode: newCustomerForm.postcode,
        country: newCustomerForm.country
      },
      active: newCustomerForm.active,
      _draft: true
    };

    setSelectedCustomer(draftCustomer);
    setSelectedCustomerOption(null);
    setSelectedVehicle(null);

    setNewCustomerForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      street: "",
      city: "",
      state: "",
      postcode: "",
      country: "",
      active: true
    });

    return true;
  };

  const handleFinish = async (identityConflictAction = null) => {
    if (savingMembership) {
      return;
    }

    const conflictAction =
      typeof identityConflictAction === "string" ? identityConflictAction : null;

    setSavingMembership(true);
    setSaveMembershipError(null);
    setIdentityConflictModal({ open: false, message: "", details: null });

    try {
      if (!selectedCustomer) {
        throw new Error("Please select or create a customer before finishing.");
      }

      if (!selectedVehicle) {
        throw new Error("Please select or create a vehicle before finishing.");
      }

      if (!selectedSubscription) {
        throw new Error("Please select or create a subscription before finishing.");
      }

      const payload = {
        customer: selectedCustomer._draft
          ? {
              firstName: selectedCustomer.firstName,
              lastName: selectedCustomer.lastName,
              email: selectedCustomer.email,
              phone: selectedCustomer.phone,
              address: selectedCustomer.address,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              active: selectedCustomer.active,
              ...(conflictAction ? { identityConflictAction: conflictAction } : {}),
            }
          : { _id: selectedCustomer._id },
        vehicle: selectedVehicle._draft
          ? {
              rego: selectedVehicle.rego,
              vin: selectedVehicle.vin,
              make: selectedVehicle.make,
              model: selectedVehicle.model,
              modelYear: selectedVehicle.modelYear,
              color: selectedVehicle.color,
              vehicleType: selectedVehicle.vehicleType || null,
              active: selectedVehicle.active
            }
          : { _id: selectedVehicle._id },
        subscription: {
          _id: selectedSubscription._draft ? undefined : selectedSubscription._id,
          product: selectedSubscription.product?._id || selectedSubscription.product?.value || selectedSubscription.product,
          startDateUTC: selectedSubscription.startDateUTC,
          validUntilDateUTC: selectedSubscription.validUntilDateUTC,
          nextBillingDateUTC: selectedSubscription.nextBillingDateUTC,
          active: selectedSubscription.active,
          createInStripe: selectedSubscription.createInStripe,
          paymentRequired: selectedSubscription.paymentRequired,
          rrp: selectedSubscription.rrp
        },
        partnerCompany: selectedPartnerCompany?._id || null,
        partnerUser: selectedPartnerUser?._id || null,
        partnerUserId: selectedPartnerUser?._id || null,
        merchantStore: selectedMerchantStore?._id || null,
        merchantStoreId: selectedMerchantStore?._id || null,
        merchantUser: selectedStoreStaff?.value || selectedStoreStaff?._id || null,
        merchantUserId: selectedStoreStaff?.value || selectedStoreStaff?._id || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      console.log("create-with-customer-vehicle payload", payload);

      const response = await api("v1/subscription/create-with-customer-vehicle", "POST", payload);

      console.log("create-with-customer-vehicle response", response);

      if (!response?.data?.success) {
        const errorCode = response?.data?.errorCode;
        const conflictData = response?.data?.data || {};
        if (
          response?.status === 409 ||
          errorCode === "CUSTOMER_IDENTITY_CONFLICT" ||
          errorCode === "PHONE_EMAIL_CONFLICT" ||
          Array.isArray(conflictData.allowedActions)
        ) {
          setIdentityConflictModal({
            open: true,
            message:
              response?.data?.message ||
              "These details match another customer. Choose how to continue.",
            details: {
              ...conflictData,
              conflictType: conflictData.conflictType || null,
            },
          });
          return;
        }
        throw new Error(response?.data?.message || "Failed to create subscription with customer and vehicle.");
      }

      const createdSubscription = response.data.data?.newSubscription || response.data.data?.subscription || null;
      if (!createdSubscription?._id) {
        throw new Error("Subscription creation response did not return an ID.");
      }

      const stripeCheckout = response.data.data?.stripeCheckout || null;
      const customerOnboarding = response.data.data?.customerOnboarding || null;

      if (stripeCheckout?.url) {
        const emailNote = stripeCheckout.paymentEmailSent
          ? "A Stripe payment link was emailed to the customer. The membership stays inactive until they complete payment — payment is required to activate it."
          : "Stripe checkout was created, but the payment email may not have sent. Share this payment link — payment is required to activate the membership.";
        setCopiedPaymentLink(false);
        setPaymentLinkModal({
          open: true,
          message: emailNote,
          url: stripeCheckout.url,
          subscriptionId: createdSubscription._id,
        });
        return;
      }

      if (customerOnboarding?.emailed) {
        const onboardingMessage = customerOnboarding.auth0Provisioned
          ? "A welcome email with their login password was sent to the customer."
          : customerOnboarding.activationLink
            ? "A welcome email with their activation link was sent to the customer. Vehicle details will be prefilled on activation."
            : "A welcome email was sent to the customer.";
        setResultSuccessModal({
          open: true,
          title: "Membership created",
          message: onboardingMessage,
          subscriptionId: createdSubscription._id,
        });
        return;
      }

      setResultSuccessModal({
        open: true,
        title: "Membership created",
        message:
          "Membership was created. Payment is treated as collected by the partner outside Stripe — no Stripe payment link was sent.",
        subscriptionId: createdSubscription._id,
      });
    } catch (error) {
      console.error("Membership save error:", error);
      const message = error?.message || "Unable to save membership. Please try again.";
      setSaveMembershipError(message);
      setResultErrorModal({ open: true, message });
    } finally {
      setSavingMembership(false);
    }
  };

  const closeIdentityConflictModal = () => {
    setIdentityConflictModal({ open: false, message: "", details: null });
  };

  const resolveIdentityConflict = (action) => {
    closeIdentityConflictModal();
    handleFinish(action);
  };

  const closePaymentLinkModal = () => {
    const subscriptionId = paymentLinkModal.subscriptionId;
    setPaymentLinkModal({ open: false, message: "", url: "", subscriptionId: null });
    setCopiedPaymentLink(false);
    if (subscriptionId) {
      navigate(`/subscription/${subscriptionId}`);
    }
  };

  const closeResultSuccessModal = () => {
    const subscriptionId = resultSuccessModal.subscriptionId;
    setResultSuccessModal({ open: false, title: "", message: "", subscriptionId: null });
    if (subscriptionId) {
      navigate(`/subscription/${subscriptionId}`);
    }
  };

  const copyPaymentLink = async () => {
    if (!paymentLinkModal.url) return;
    try {
      await navigator.clipboard.writeText(paymentLinkModal.url);
      setCopiedPaymentLink(true);
    } catch (error) {
      console.error("Failed to copy payment link:", error);
      setCopiedPaymentLink(false);
    }
  };

  const subscriptionLabel = (subscription) => {
    const productName = subscription?.product?.name || "Membership";
    const vehicleLabel = subscription?.vehicle?.rego || subscription?.vehicle?.vin || "No vehicle";
    return `${productName} • ${vehicleLabel}`;
  };

  const formatReviewDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const getSubscriptionReviewLines = (subscription) => {
    if (!subscription) {
      return ["No subscription selected"];
    }

    const product = subscription.product;
    const productName = product?.name || "Membership";
    const productPrice =
      product?.price != null && product?.price !== ""
        ? `$${product.price}`
        : null;
    const productDuration = product?.subscriptionDuration || product?.billingInterval || null;
    const rrp =
      subscription.rrp != null && subscription.rrp !== ""
        ? `$${subscription.rrp}`
        : null;

    return [
      productDuration ? `${productName} (${productDuration})` : productName,
      productPrice ? `Product price: ${productPrice}` : null,
      `Start date: ${formatReviewDate(subscription.startDateUTC)}`,
      `Valid until: ${formatReviewDate(subscription.validUntilDateUTC)}`,
      `Next billing: ${formatReviewDate(subscription.nextBillingDateUTC)}`,
      `Status: ${
        subscription.createInStripe || subscription.handledByStripe
          ? "Inactive until Stripe payment"
          : subscription.active
            ? "Active"
            : "Inactive"
      }`,
      subscription.createInStripe || subscription.handledByStripe
        ? "Stripe: Yes • Payment link required to activate membership"
        : "Stripe: No • Payment collected by partner",
      rrp ? `Amount for customer to pay: ${rrp}` : null,
      subscription._draft ? "New subscription (will be created on finish)" : null,
    ].filter(Boolean);
  };

  return (
    <div className="page subscription-create-membership w-full flex flex-column items-center p-4">
      <div className="pageTitle flex flex-col w-full text-center mb-4 lg:flex-row lg:text-left lg:justify-between">
        <div>
          <h2>Create Membership</h2>
          <p className="text-gray-600 mt-2">Create a membership in a guided flow with customer and subscription selection.</p>
        </div>
        <div className="flex gap-2">
          <IconButton onClick={() => navigate("/subscription")} sx={{ border: "1px solid grey" }}>
            <ArrowBackIcon />
          </IconButton>
        </div>
      </div>

      <div className="w-full max-w-6xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap gap-3 mb-6">
          {[1, 2, 3, 4, 5].map((item) => {
            const active = step >= item;
            const current = step === item;
            return (
              <div key={item} className={`flex-1 min-w-[140px] rounded-xl border px-4 py-3 ${active ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
                <div className={`text-sm font-semibold ${current ? "text-blue-700" : "text-slate-700"}`}>
                  Step {item}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {item === 1 && "Customer"}
                  {item === 2 && "Vehicle"}
                  {item === 3 && "Subscription"}
                  {item === 4 && "Partner company"}
                  {item === 5 && "Review"}
                </div>
              </div>
            );
          })}
        </div>

        {step === 1 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold mb-2">Step 1 • Choose customer</h3>
              <p className="text-sm text-slate-600">Select an existing customer or create a new one to continue.</p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <button
                type="button"
                onClick={() => setCustomerMode("existing")}
                className={`rounded-2xl border p-4 text-left transition ${customerMode === "existing" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Use existing customer</div>
                <div className="text-sm text-slate-600 mt-1">Search by name, email, or phone and continue.</div>
              </button>

              <button
                type="button"
                onClick={() => setCustomerMode("new")}
                className={`rounded-2xl border p-4 text-left transition ${customerMode === "new" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Add new customer</div>
                <div className="text-sm text-slate-600 mt-1">Fill the customer form inline and continue.</div>
              </button>
            </div>

            {customerMode === "existing" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Search existing customer</label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    isClearable
                    isLoading={loadingCustomers}
                    loadOptions={loadCustomerOptions}
                    onChange={handleCustomerSelect}
                    value={selectedCustomerOption}
                    placeholder="Type name, email, or phone…"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "No customers found" : "Start typing to search customers"
                    }
                    styles={customerSelectStyles}
                    formatOptionLabel={(option) => (
                      <div className="py-1">
                        <div className="font-medium text-slate-800">{option.label}</div>
                        {(option.meta?.phone || option.meta?.address) && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {[option.meta.phone, option.meta.address].filter(Boolean).join(" • ")}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
                {selectedCustomer && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-1">
                    <div className="font-semibold">{customerLabel(selectedCustomer)}</div>
                    {selectedCustomer.phone && <div>Phone: {selectedCustomer.phone}</div>}
                    {selectedCustomer.address && (
                      <div>
                        Address:{" "}
                        {[
                          selectedCustomer.address.street,
                          selectedCustomer.address.city,
                          selectedCustomer.address.state,
                          selectedCustomer.address.postcode
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {customerMode === "new" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">New customer details</label>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="firstName">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.firstName}
                      onChange={(e) => handleNewCustomerFieldChange("firstName", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.lastName}
                      onChange={(e) => handleNewCustomerFieldChange("lastName", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.email}
                      onChange={(e) => handleNewCustomerFieldChange("email", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      id="phone"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.phone}
                      onChange={(e) => handleNewCustomerFieldChange("phone", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="street">
                      Street
                    </label>
                    <input
                      id="street"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.street}
                      onChange={(e) => handleNewCustomerFieldChange("street", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="city">
                      City
                    </label>
                    <input
                      id="city"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.city}
                      onChange={(e) => handleNewCustomerFieldChange("city", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="state">
                      State
                    </label>
                    <input
                      id="state"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.state}
                      onChange={(e) => handleNewCustomerFieldChange("state", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="postcode">
                      Postcode
                    </label>
                    <input
                      id="postcode"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.postcode}
                      onChange={(e) => handleNewCustomerFieldChange("postcode", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="country">
                      Country
                    </label>
                    <input
                      id="country"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newCustomerForm.country}
                      onChange={(e) => handleNewCustomerFieldChange("country", e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newCustomerForm.active}
                      onChange={(e) => handleNewCustomerFieldChange("active", e.target.checked)}
                    />
                    Active
                  </label>
                </div>

                {newCustomerError && <p className="text-sm text-red-600">{newCustomerError}</p>}
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="contained" sx={{ backgroundColor: COLORS.priorityBlue.hex }} onClick={goToStepTwo}>
                Continue to vehicle
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold mb-2">Step 2 • Choose vehicle</h3>
              <p className="text-sm text-slate-600">Select an existing vehicle or add a new one for this customer.</p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <button
                type="button"
                onClick={() => setVehicleMode("existing")}
                className={`rounded-2xl border p-4 text-left transition ${vehicleMode === "existing" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Use existing vehicle</div>
                <div className="text-sm text-slate-600 mt-1">Pick a vehicle already attached to the selected customer.</div>
              </button>

              <button
                type="button"
                onClick={() => setVehicleMode("new")}
                className={`rounded-2xl border p-4 text-left transition ${vehicleMode === "new" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Add new vehicle</div>
                <div className="text-sm text-slate-600 mt-1">Fill the new vehicle form inline and continue.</div>
              </button>
            </div>

            {vehicleMode === "existing" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Existing vehicle</label>
                  {loadingCustomerVehicles ? (
                    <p className="text-sm text-slate-500">Loading vehicles...</p>
                  ) : customerVehicles.length > 0 ? (
                    <select
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={selectedVehicle?._id || ""}
                      onChange={(event) => {
                        const vehicle = customerVehicles.find((item) => item._id === event.target.value) || null;
                        setSelectedVehicle(vehicle);
                      }}
                    >
                      <option value="">Select a vehicle</option>
                      {customerVehicles.map((vehicle) => (
                        <option key={vehicle._id} value={vehicle._id}>
                          {vehicle.rego || vehicle.vin || `${vehicle.make} ${vehicle.model}`} {vehicle.modelYear ? `• ${vehicle.modelYear}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-slate-500">No vehicles found for this customer.</p>
                  )}
                </div>
              </div>
            )}

            {vehicleMode === "new" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">New vehicle details</label>
                  <p className="text-sm text-slate-600">
                    Enter the rego and state first so we can auto-find the vehicle information for you.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="rego">
                      REGO
                    </label>
                    <input
                      id="rego"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.rego}
                      onChange={(e) => handleNewVehicleFieldChange("rego", e.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="vehicleState">
                      {getVehicleStateFieldLabel()}
                    </label>
                    <select
                      id="vehicleState"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.state}
                      onChange={(e) => handleNewVehicleFieldChange("state", e.target.value)}
                    >
                      <option value="">Select {getVehicleStateFieldLabel().toLowerCase()}</option>
                      {getVehicleStateOptionsForSelect(newVehicleForm.state).map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {vehicleLookupStatus && <p className="text-sm text-slate-600">{vehicleLookupStatus}</p>}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="vin">
                      VIN
                    </label>
                    <input
                      id="vin"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.vin}
                      onChange={(e) => handleNewVehicleFieldChange("vin", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="vehicleType">
                      Vehicle Type
                    </label>
                    <select
                      id="vehicleType"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.vehicleType}
                      onChange={(e) => handleNewVehicleFieldChange("vehicleType", e.target.value)}
                    >
                      <option value="">Select vehicle type</option>
                      {VEHICLE_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="make">
                      Make
                    </label>
                    <input
                      id="make"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.make}
                      onChange={(e) => handleNewVehicleFieldChange("make", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="model">
                      Model
                    </label>
                    <input
                      id="model"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.model}
                      onChange={(e) => handleNewVehicleFieldChange("model", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="modelYear">
                      Model Year
                    </label>
                    <input
                      id="modelYear"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.modelYear}
                      onChange={(e) => handleNewVehicleFieldChange("modelYear", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="block text-sm font-semibold mb-1" htmlFor="color">
                      Color
                    </label>
                    <input
                      id="color"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={newVehicleForm.color}
                      onChange={(e) => handleNewVehicleFieldChange("color", e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newVehicleForm.active}
                      onChange={(e) => handleNewVehicleFieldChange("active", e.target.checked)}
                    />
                    Active
                  </label>
                </div>
                {newVehicleError && <p className="text-sm text-red-600">{newVehicleError}</p>}
                <p className="text-xs text-slate-500">The vehicle will be saved as a draft and reviewed on the next step.</p>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outlined" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={() => {
                  if (vehicleMode === "existing" && !selectedVehicle) {
                    alert("Please select an existing vehicle or switch to add a new one.");
                    return;
                  }
                  if (vehicleMode === "new" && !selectedVehicle) {
                    const created = handleCreateVehicle();
                    if (!created) {
                      return;
                    }
                  }
                  setStep(3);
                }}
              >
                Continue to subscription
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold mb-2">Step 3 • Choose subscription</h3>
              <p className="text-sm text-slate-600">Choose an existing membership or create a new one for this vehicle.</p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <button
                type="button"
                onClick={() => setSubscriptionMode("existing")}
                className={`rounded-2xl border p-4 text-left transition ${subscriptionMode === "existing" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Use existing subscription</div>
                <div className="text-sm text-slate-600 mt-1">Pick a subscription already linked to this customer.</div>
              </button>

              <button
                type="button"
                onClick={() => setSubscriptionMode("new")}
                className={`rounded-2xl border p-4 text-left transition ${subscriptionMode === "new" ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white"}`}
              >
                <div className="font-semibold">Add new subscription</div>
                <div className="text-sm text-slate-600 mt-1">Fill the subscription form inline and continue.</div>
              </button>
            </div>

            {subscriptionMode === "existing" && (
              <div className="rounded-xl border border-slate-200 p-4">
                <label className="block text-sm font-semibold mb-2">Existing subscription</label>
                {loadingSubscriptions ? (
                  <p className="text-sm text-slate-500">Loading subscriptions...</p>
                ) : subscriptionOptions.length > 0 ? (
                  <>
                    <div className="relative">
                      <select
                        className="w-full appearance-none rounded border border-slate-300 bg-white px-3 py-2 pr-10 shadow-sm"
                        value={selectedSubscription?._id || ""}
                        onChange={handleSubscriptionSelect}
                      >
                        <option value="">Select a membership</option>
                        {subscriptionOptions.map((subscription) => (
                          <option key={subscription._id} value={subscription._id}>
                            {subscriptionLabel(subscription)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-500">
                        ▼
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Choose one of the current memberships or switch to create a new one.</p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">No existing subscriptions were found for this customer.</p>
                )}
              </div>
            )}

            {subscriptionMode === "new" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div className="text-sm text-slate-600">Creating a new subscription for {selectedVehicle ? `${selectedVehicle.rego || selectedVehicle.vin || `${selectedVehicle.make} ${selectedVehicle.model}`}` : "the selected vehicle"}.</div>
                {loadingProducts ? (
                  <p className="text-sm text-slate-500">Loading products...</p>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="form-group md:col-span-2">
                      <label className="block text-sm font-semibold mb-1" htmlFor="productSearch">
                        Product
                      </label>
                      <div className="space-y-3">
                        <input
                          id="productSearch"
                          type="text"
                          className="w-full rounded border border-slate-300 px-3 py-2"
                          placeholder="Search product by name…"
                          value={productSearch}
                          onChange={(e) => handleProductSearchChange(e.target.value)}
                        />
                        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                          {filteredProductOptions.length > 0 ? (
                            filteredProductOptions.map((product) => {
                              const isSelected = newSubscriptionForm.productId === product._id;
                              return (
                                <button
                                  key={product._id}
                                  type="button"
                                  onClick={() => handleProductSelect(product)}
                                  className={`w-full text-left px-3 py-3 transition ${
                                    isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-50"
                                  }`}
                                >
                                  <div className="font-medium text-slate-800">{product.name}</div>
                                  {product.price != null && product.price !== "" && (
                                    <div className="text-xs text-slate-500 mt-0.5">${product.price}</div>
                                  )}
                                </button>
                              );
                            })
                          ) : (
                            <div className="px-3 py-4 text-sm text-slate-500">
                              No products match “{productSearch}”.
                            </div>
                          )}
                        </div>
                        {selectedProduct && (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                            Selected: <span className="font-semibold">{selectedProduct.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="block text-sm font-semibold mb-1" htmlFor="startDateUTC">
                        Start Date
                      </label>
                      <input
                        id="startDateUTC"
                        type="date"
                        className="w-full rounded border border-slate-300 px-3 py-2"
                        value={newSubscriptionForm.startDateUTC}
                        onChange={(e) => handleNewSubscriptionFieldChange("startDateUTC", e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="block text-sm font-semibold mb-1" htmlFor="validUntilDateUTC">
                        Valid Until Date
                      </label>
                      <input
                        id="validUntilDateUTC"
                        type="date"
                        className="w-full rounded border border-slate-300 px-3 py-2"
                        value={newSubscriptionForm.validUntilDateUTC}
                        onChange={(e) => handleNewSubscriptionFieldChange("validUntilDateUTC", e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="block text-sm font-semibold mb-1" htmlFor="nextBillingDateUTC">
                        Next Billing Date
                      </label>
                      <input
                        id="nextBillingDateUTC"
                        type="date"
                        className="w-full rounded border border-slate-300 px-3 py-2"
                        value={newSubscriptionForm.nextBillingDateUTC}
                        onChange={(e) => handleNewSubscriptionFieldChange("nextBillingDateUTC", e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group md:col-span-2">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newSubscriptionForm.handledByStripe}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            handleNewSubscriptionFieldChange("handledByStripe", checked);
                            // Stripe always requires payment via link; membership stays inactive until paid
                            handleNewSubscriptionFieldChange("paymentRequired", checked);
                            handleNewSubscriptionFieldChange("active", !checked);
                          }}
                        />
                        Create in Stripe
                      </label>
                      <p className="text-xs text-slate-500 mt-1">
                        {newSubscriptionForm.handledByStripe
                          ? "Payment via Stripe link is required to activate this membership. It stays inactive until the customer pays."
                          : "Payment is collected by the partner outside Stripe. Membership is created active (no Stripe payment link)."}
                      </p>
                    </div>
                    {newSubscriptionForm.handledByStripe && (
                      <div className="form-group md:col-span-2">
                        <label className="block text-sm font-semibold mb-1" htmlFor="rrp">
                          Amount for customer to pay (AUD)
                        </label>
                        <input
                          id="rrp"
                          type="number"
                          className="w-full rounded border border-slate-300 px-3 py-2"
                          value={newSubscriptionForm.rrp}
                          onChange={(e) => handleNewSubscriptionFieldChange("rrp", e.target.value)}
                          min="0"
                          step="0.01"
                          required
                          placeholder="Enter the amount the customer will pay via Stripe"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          This is the amount charged on the Stripe payment link.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {newSubscriptionError && <p className="text-sm text-red-600">{newSubscriptionError}</p>}
                <p className="text-xs text-slate-500">No separate save button is required. The membership will be reviewed on the next step.</p>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outlined" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={() => {
                  if (subscriptionMode === "existing" && !selectedSubscription) {
                    alert("Please select an existing subscription or choose to add a new one.");
                    return;
                  }
                  if (subscriptionMode === "new") {
                    if (!selectedSubscription) {
                      const didCreate = handleCreateSubscriptionDraft();
                      if (!didCreate) {
                        return;
                      }
                    }
                  }
                  setStep(4);
                }}
              >
                Continue to partner company
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold mb-2">Step 4 • Choose partner company</h3>
              <p className="text-sm text-slate-600">
                {selectedSubscription?.createInStripe || selectedSubscription?.paymentRequired
                  ? "Partner/store details are optional here. If none are selected, the transaction is linked to the Priority One Internal partner account."
                  : "Select a partner company, then the merchant store and store staff so the transaction is linked correctly."}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" htmlFor="partnerCompanySearch">
                  Partner company
                </label>
                {loadingPartnerCompanies ? (
                  <p className="text-sm text-slate-500">Loading partner companies...</p>
                ) : (
                  <div className="space-y-3">
                    <input
                      id="partnerCompanySearch"
                      type="text"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      placeholder="Search partner company by name…"
                      value={partnerCompanySearch}
                      onChange={(e) => handlePartnerCompanySearchChange(e.target.value)}
                    />
                    <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {filteredPartnerCompanies.length > 0 ? (
                        filteredPartnerCompanies.map((company) => {
                          const isSelected = selectedPartnerCompany?._id === company._id;
                          return (
                            <button
                              key={company._id}
                              type="button"
                              onClick={() => handlePartnerCompanySelect(company)}
                              className={`w-full text-left px-3 py-3 transition ${
                                isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-50"
                              }`}
                            >
                              <div className="font-medium text-slate-800">{company.name}</div>
                              {(company.email || company.phone) && (
                                <div className="text-xs text-slate-500 mt-0.5">
                                  {[company.email, company.phone].filter(Boolean).join(" • ")}
                                </div>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-sm text-slate-500">
                          No partner companies match “{partnerCompanySearch}”.
                        </div>
                      )}
                    </div>
                    {selectedPartnerCompany && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        Selected company: <span className="font-semibold">{selectedPartnerCompany.name}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedPartnerCompany && (
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="merchantStoreSearch">
                    Merchant store
                  </label>
                  {loadingMerchantStores ? (
                    <p className="text-sm text-slate-500">Loading stores...</p>
                  ) : (
                    <div className="space-y-3">
                      <input
                        id="merchantStoreSearch"
                        type="text"
                        className="w-full rounded border border-slate-300 px-3 py-2"
                        placeholder="Search store by name or address…"
                        value={merchantStoreSearch}
                        onChange={(e) => handleMerchantStoreSearchChange(e.target.value)}
                      />
                      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                        {filteredMerchantStores.length > 0 ? (
                          filteredMerchantStores.map((store) => {
                            const isSelected = selectedMerchantStore?._id === store._id;
                            return (
                              <button
                                key={store._id}
                                type="button"
                                onClick={() => handleMerchantStoreSelect(store)}
                                className={`w-full text-left px-3 py-3 transition ${
                                  isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-50"
                                }`}
                              >
                                <div className="font-medium text-slate-800">{store.name}</div>
                                {storeAddressLabel(store) && (
                                  <div className="text-xs text-slate-500 mt-0.5">{storeAddressLabel(store)}</div>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-sm text-slate-500">
                            No stores match “{merchantStoreSearch}”.
                          </div>
                        )}
                      </div>
                      {selectedMerchantStore && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          Selected store: <span className="font-semibold">{selectedMerchantStore.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedMerchantStore && (
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="storeStaffSearch">
                    Store staff
                  </label>
                  <div className="space-y-3">
                    <input
                      id="storeStaffSearch"
                      type="text"
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      placeholder="Search store staff by name…"
                      value={storeStaffSearch}
                      onChange={(e) => handleStoreStaffSearchChange(e.target.value)}
                    />
                    <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {filteredStoreStaff.length > 0 ? (
                        filteredStoreStaff.map((staff) => {
                          const staffId = staff.value || staff._id;
                          const isSelected =
                            (selectedStoreStaff?.value || selectedStoreStaff?._id) === staffId;
                          return (
                            <button
                              key={staffId}
                              type="button"
                              onClick={() => handleStoreStaffSelect(staff)}
                              className={`w-full text-left px-3 py-3 transition ${
                                isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-50"
                              }`}
                            >
                              <div className="font-medium text-slate-800">{storeStaffLabel(staff)}</div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-sm text-slate-500">
                          {storeStaff.length === 0
                            ? "No store staff found for this store."
                            : `No store staff match “${storeStaffSearch}”.`}
                        </div>
                      )}
                    </div>
                    {selectedStoreStaff && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        Selected staff: <span className="font-semibold">{storeStaffLabel(selectedStoreStaff)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedPartnerCompany && partnerUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="partnerUserSearch">
                    Partner user (optional)
                  </label>
                  {loadingPartnerUsers ? (
                    <p className="text-sm text-slate-500">Loading partner users...</p>
                  ) : (
                    <div className="space-y-3">
                      <input
                        id="partnerUserSearch"
                        type="text"
                        className="w-full rounded border border-slate-300 px-3 py-2"
                        placeholder="Search partner user by name, email, or phone…"
                        value={partnerUserSearch}
                        onChange={(e) => handlePartnerUserSearchChange(e.target.value)}
                      />
                      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                        {filteredPartnerUsers.length > 0 ? (
                          filteredPartnerUsers.map((user) => {
                            const isSelected = selectedPartnerUser?._id === user._id;
                            return (
                              <button
                                key={user._id}
                                type="button"
                                onClick={() => handlePartnerUserSelect(user)}
                                className={`w-full text-left px-3 py-3 transition ${
                                  isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-white hover:bg-slate-50"
                                }`}
                              >
                                <div className="font-medium text-slate-800">{partnerUserLabel(user)}</div>
                                {(user.email || user.phone) && (
                                  <div className="text-xs text-slate-500 mt-0.5">
                                    {[user.email, user.phone].filter(Boolean).join(" • ")}
                                  </div>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-sm text-slate-500">
                            No partner users match “{partnerUserSearch}”.
                          </div>
                        )}
                      </div>
                      {selectedPartnerUser && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          Selected partner user:{" "}
                          <span className="font-semibold">{partnerUserLabel(selectedPartnerUser)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700">Customer</div>
                  <div className="text-sm text-slate-600">
                    {selectedCustomer ? customerLabel(selectedCustomer) : "No customer selected"}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-700">Subscription</div>
                  <div className="text-sm text-slate-600 space-y-1">
                    {getSubscriptionReviewLines(selectedSubscription).map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-700">Vehicle</div>
                  <div className="text-sm text-slate-600">
                    {selectedVehicle ? `${selectedVehicle.rego || selectedVehicle.vin || "Vehicle"} ${selectedVehicle.modelYear ? `• ${selectedVehicle.modelYear}` : ""}` : "No vehicle selected"}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outlined" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={() => {
                  const paymentViaStripe = Boolean(
                    selectedSubscription?.createInStripe || selectedSubscription?.paymentRequired
                  );

                  if (!paymentViaStripe) {
                    if (!selectedPartnerCompany) {
                      alert("Please select a partner company before continuing.");
                      return;
                    }
                    if (!selectedMerchantStore) {
                      alert("Please select a merchant store before continuing.");
                      return;
                    }
                    if (!selectedStoreStaff) {
                      alert("Please select store staff for the selected store.");
                      return;
                    }
                  } else if (selectedMerchantStore && !selectedStoreStaff) {
                    alert("Please select store staff for the selected store, or clear the store selection.");
                    return;
                  }

                  setStep(5);
                }}
              >
                Continue to review
              </Button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold mb-2">Step 5 • Review</h3>
              <p className="text-sm text-slate-600">Your membership is ready to save. Review the selections and continue.</p>
            </div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">Customer</div>
                <div className="text-sm text-slate-600">
                  {selectedCustomer ? customerLabel(selectedCustomer) : "No customer selected"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Subscription</div>
                <div className="text-sm text-slate-600 space-y-1">
                  {getSubscriptionReviewLines(selectedSubscription).map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Vehicle</div>
                <div className="text-sm text-slate-600">
                  {selectedVehicle
                    ? `${selectedVehicle.rego || selectedVehicle.vin || "Vehicle"}${selectedVehicle.modelYear ? ` • ${selectedVehicle.modelYear}` : ""}${
                        selectedVehicle.vehicleType
                          ? ` • ${VEHICLE_TYPE_OPTIONS.find((option) => option.value === selectedVehicle.vehicleType)?.label || selectedVehicle.vehicleType}`
                          : ""
                      }`
                    : "No vehicle selected"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Partner company</div>
                <div className="text-sm text-slate-600">
                  {selectedPartnerCompany
                    ? selectedPartnerCompany.name
                    : selectedSubscription?.createInStripe || selectedSubscription?.paymentRequired
                      ? "Priority One Internal (default for Stripe payment link)"
                      : "No partner company selected"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Merchant store</div>
                <div className="text-sm text-slate-600">
                  {selectedMerchantStore
                    ? selectedMerchantStore.name
                    : selectedSubscription?.createInStripe || selectedSubscription?.paymentRequired
                      ? "Not required for Stripe payment link"
                      : "No store selected"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Store staff</div>
                <div className="text-sm text-slate-600">
                  {selectedStoreStaff
                    ? storeStaffLabel(selectedStoreStaff)
                    : selectedSubscription?.createInStripe || selectedSubscription?.paymentRequired
                      ? "Not required for Stripe payment link"
                      : "No store staff selected"}
                </div>
              </div>
              {selectedPartnerUser && (
                <div>
                  <div className="text-sm font-semibold text-slate-700">Partner user</div>
                  <div className="text-sm text-slate-600">{partnerUserLabel(selectedPartnerUser)}</div>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="contained" sx={{ backgroundColor: COLORS.priorityBlue.hex }} onClick={() => handleFinish()}>
                Finish
              </Button>
            </div>
          </div>
        )}

      </div>


      <ModalPopup
        showModal={paymentLinkModal.open}
        handleShowModal={closePaymentLinkModal}
        popupTitle="Payment link ready"
        popupContent={
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{paymentLinkModal.message}</p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 break-all text-sm text-slate-700">
              {paymentLinkModal.url}
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outlined" onClick={copyPaymentLink}>
                {copiedPaymentLink ? "Copied" : "Copy link"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (paymentLinkModal.url) {
                    window.open(paymentLinkModal.url, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                Open link
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={closePaymentLinkModal}
              >
                Continue
              </Button>
            </div>
          </div>
        }
      />

      <ModalPopup
        showModal={resultSuccessModal.open}
        handleShowModal={closeResultSuccessModal}
        popupTitle={resultSuccessModal.title || "Membership created"}
        popupContent={
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{resultSuccessModal.message}</p>
            <div className="flex justify-end">
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={closeResultSuccessModal}
              >
                Continue
              </Button>
            </div>
          </div>
        }
      />

      <ModalPopup
        showModal={identityConflictModal.open}
        handleShowModal={closeIdentityConflictModal}
        popupTitle="Customer details conflict"
        popupContent={
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{identityConflictModal.message}</p>
            {identityConflictModal.details?.conflictType === "email" &&
              identityConflictModal.details?.existingPhone &&
              identityConflictModal.details?.requestedPhone && (
                <p className="text-sm text-slate-500">
                  Existing mobile on file: <strong>{identityConflictModal.details.existingPhone}</strong>
                  <br />
                  New signup mobile: <strong>{identityConflictModal.details.requestedPhone}</strong>
                </p>
              )}
            {identityConflictModal.details?.conflictType !== "email" &&
              identityConflictModal.details?.existingEmail &&
              identityConflictModal.details?.requestedEmail && (
                <p className="text-sm text-slate-500">
                  Existing account email: <strong>{identityConflictModal.details.existingEmail}</strong>
                  <br />
                  New signup email: <strong>{identityConflictModal.details.requestedEmail}</strong>
                </p>
              )}
            <p className="text-sm text-slate-500">
              <strong>Merge</strong> updates the existing Priority One customer to match this signup.{" "}
              <strong>Create new customer</strong> keeps the other record unchanged and creates a separate customer.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outlined" onClick={closeIdentityConflictModal} disabled={savingMembership}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => resolveIdentityConflict("create_new")}
                disabled={savingMembership}
              >
                Create new customer
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={() => resolveIdentityConflict("merge")}
                disabled={savingMembership}
              >
                Merge into existing
              </Button>
            </div>
          </div>
        }
      />

      <ModalPopup
        showModal={resultErrorModal.open}
        handleShowModal={() => setResultErrorModal({ open: false, message: "" })}
        popupTitle="Unable to save membership"
        popupContent={
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{resultErrorModal.message}</p>
            <div className="flex justify-end">
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={() => setResultErrorModal({ open: false, message: "" })}
              >
                OK
              </Button>
            </div>
          </div>
        }
      />

      <ModalPopup
        showModal={showVehicleModal}
        handleShowModal={() => setShowVehicleModal(false)}
        popupTitle="Add New Vehicle"
        popupContent={
          <form className="space-y-4" onSubmit={handleCreateVehicle}>
            <p className="text-sm text-slate-600">
              Adding a vehicle for {selectedCustomer ? customerLabel(selectedCustomer) : "the selected customer"}.
            </p>
            <p className="text-sm text-slate-600">
              Enter the rego and state first so we can auto-find the vehicle information for you.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-rego">
                  REGO
                </label>
                <input
                  id="modal-rego"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.rego}
                  onChange={(e) => handleNewVehicleFieldChange("rego", e.target.value.toUpperCase())}
                  required
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-vehicleState">
                  {getVehicleStateFieldLabel()}
                </label>
                <select
                  id="modal-vehicleState"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.state}
                  onChange={(e) => handleNewVehicleFieldChange("state", e.target.value)}
                  required
                >
                  <option value="">Select {getVehicleStateFieldLabel().toLowerCase()}</option>
                  {getVehicleStateOptionsForSelect(newVehicleForm.state).map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {vehicleLookupStatus && <p className="text-sm text-slate-600">{vehicleLookupStatus}</p>}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-vin">
                  VIN
                </label>
                <input
                  id="modal-vin"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.vin}
                  onChange={(e) => handleNewVehicleFieldChange("vin", e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-vehicleType">
                  Vehicle Type
                </label>
                <select
                  id="modal-vehicleType"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.vehicleType}
                  onChange={(e) => handleNewVehicleFieldChange("vehicleType", e.target.value)}
                  required
                >
                  <option value="">Select vehicle type</option>
                  {VEHICLE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-make">
                  Make
                </label>
                <input
                  id="modal-make"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.make}
                  onChange={(e) => handleNewVehicleFieldChange("make", e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-model">
                  Model
                </label>
                <input
                  id="modal-model"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.model}
                  onChange={(e) => handleNewVehicleFieldChange("model", e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-modelYear">
                  Model Year
                </label>
                <input
                  id="modal-modelYear"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.modelYear}
                  onChange={(e) => handleNewVehicleFieldChange("modelYear", e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="block text-sm font-semibold mb-1" htmlFor="modal-color">
                  Color
                </label>
                <input
                  id="modal-color"
                  className="w-full rounded border border-slate-300 px-3 py-2"
                  value={newVehicleForm.color}
                  onChange={(e) => handleNewVehicleFieldChange("color", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newVehicleForm.active}
                  onChange={(e) => handleNewVehicleFieldChange("active", e.target.checked)}
                />
                Active
              </label>
            </div>
            {newVehicleError && <p className="text-sm text-red-600">{newVehicleError}</p>}
            <div className="flex justify-end gap-3">
              <Button variant="outlined" onClick={() => setShowVehicleModal(false)} disabled={vehicleSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={vehicleSubmitting}>
                {vehicleSubmitting ? "Saving..." : "Save vehicle"}
              </Button>
            </div>
          </form>
        }
      />

    </div>
  );
};

export default CreateMembership;
